import React, { forwardRef, memo, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ReactSortable } from 'react-sortablejs';
import 'swiper/css';
import { Swiper, SwiperSlide } from 'swiper/react';
import { CancelIcon, CheckMarkIcon, FullScreenIcon, ImagineLogo, LeftIcon, RightIcon } from '../../assets/icons';
import { Download, FooterButton } from '../../common';
import ImagePreviewSlider from '../../common/ImagePreviewSlider';
import MenuDropdown from '../../common/MenuDropdown';
import BinIcon from '../../components/icons/BinIcon';
import { EditIcon } from '../../components/icons/EditIcon';
import { DOWNLOAD_ICON } from '../../constants/assetConstants';
import { PRODUCT_DETAILS_PAGE_LABEL, PRODUCT_ROOM_TYPE_VALUE } from '../../constants/constants';
import { ROOM_TYPE } from '../../constants/roomConstants';
import { useUserContext } from '../../contexts/UserContext';
import {
  apiDeleteMultipleProductImages,
  apiDeleteProductImages,
  apiGetProductRendersById,
  apiGetProductRendersByIdPublic,
  apiUpdateProductImageOrder,
  apiUpdateProductImageName,
  apiGetProductGifsById,
  apiGetProductGifsByIdPublic,
  apiDeleteProductGifs,
  apiUpdateProductGifs,
} from '../../helpers/api';
import { fetchAllPages } from '../../helpers/apiHelper';
import { getFormData, handleDownloadImage, handleToggleScreen } from '../../helpers/jsHelper';
import { successToast, warningToast } from '../../helpers/toastHelper';
import { useInfoDialogaActions } from '../../hooks/useInfoDialogaActions';
import Button from '../../components/Button/Button';
import { PASS } from '../../constants/renderPreviewConstants';

export const ImageryShowCase = memo(
  ({
    title = 'Imagery',
    imageType = ROOM_TYPE.LIFESTYLE,
    headerClassName = '',
    setInitialTab,
    setType,
    setShowModal,
    setImageListForOpenModalType,
    productId,
    refresh,
    setRefresh,
    isPublicPage,
    filteredSeparateActionMenu,
    handleOpenCurator,
    getImageSrc,
    getImageTypeFooterComponent,
    actionMenu,
  }) => {
    const dispatch = useDispatch();
    const { currentUser, isGuestUser, canUpdatePdpV1Content } = useUserContext();
    const { openInfoDialog } = useInfoDialogaActions();
    const [isOpen, setIsOpen] = useState(false);
    const [photoIndex, setPhotoIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const [imageList, setImageList] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [pass, setPass] = useState(PASS.STANDARD);
    const [renameValue, setRenameValue] = useState(null);
    const [isMobile, setIsMobile] = useState(false);
    const [browserWidth, setBrowserWidth] = useState(0);

    const setWindowDimensions = () => {
      if (+document?.documentElement?.clientWidth > 767) {
        setIsMobile(false);
      } else {
        setIsMobile(true);
      }
      setBrowserWidth(document?.documentElement?.clientWidth);
    };

    useEffect(() => {
      setWindowDimensions();
      window.addEventListener('resize', setWindowDimensions);
      return () => {
        window.removeEventListener('resize', setWindowDimensions);
      };
    }, []);

    useEffect(() => {
      fetchRenderData();
    }, [page]);

    useEffect(() => {
      if (
        refresh &&
        refresh ===
          (imageType === ROOM_TYPE.LIFESTYLE
            ? PRODUCT_ROOM_TYPE_VALUE.LIFESTYLE
            : PRODUCT_ROOM_TYPE_VALUE.VIGNETTE)
      ) {
        if (page === 1) {
          fetchRenderData();
        } else {
          setPage(1);
        }
        setRefresh(null);
      }
    }, [refresh]);

    const fetchRenderData = async () => {
      const key = isPublicPage ? 'unicode' : 'product';
      const payload = {
        [key]: productId,
        room_type: imageType, // ROOM_TYPE?.LIFESTYLE,
        page: page,
        per_page: 10,
      };
      setLoading(true);
      const apiGif = isPublicPage ? apiGetProductGifsByIdPublic : apiGetProductGifsById;
      const api = isPublicPage ? apiGetProductRendersByIdPublic : apiGetProductRendersById;
      // const result = await api(payload);

      const result = await fetchAllPages((page) => {
        return api({
          [key]: productId,
          room_type: imageType, // ROOM_TYPE?.LIFESTYLE,
          page: page,
          per_page: 20,
        });
      });

      const resultGif = await fetchAllPages((page) => {
        return apiGif({
          [key]: productId,
          room_type: imageType, // ROOM_TYPE?.LIFESTYLE,
          page: page,
          per_page: 20,
        });
      });

      setLoading(false);
      setHasMore(result?.next_link ? true : false);
      let _pass = {};
      result?.results?.forEach(
        (_item) =>
          (_pass[_item.id] = {
            default: _item.render_image_details?.img_type,
            current: _item.render_image_details?.img_type,
          })
      );

      if (page > 1) {
        setImageList([...imageList, ...result?.results]);
        setPass({ ...pass, ..._pass });
      } else {
        setImageList([...resultGif?.results, ...result?.results]);
        setPass(_pass);
      }
    };

    // const fetchMoreData = () => {
    //   if (hasMore) {
    //     setPage(page + 1);
    //   }
    // };

    const footerActions = [
      {
        element: Download,
        title: 'Download',
        footerButton: true,
        iconSrc: DOWNLOAD_ICON,
        clickFunction: () =>
          handleDownloadImage({
            url: getImageSrc(imageList?.[photoIndex], pass),
            name: imageList?.[photoIndex]?.name,
          }),
        seq: 1,
        permissionsRequired: false,
      },
      {
        element: FooterButton,
        title: 'Edit in 3D',
        footerButton: currentUser,
        icon: ImagineLogo,
        clickFunction: () =>
          handleSelect(
            { value: PRODUCT_DETAILS_PAGE_LABEL.EDIT_IN_3D_VALUE },
            imageList?.[photoIndex]
          ),
        seq: 2,
        hideInCaseOf: isGuestUser(),
        permissionsRequired: false,
      },
      {
        element: FooterButton,
        title: 'Full screen',
        footerButton: true,
        icon: FullScreenIcon,
        clickFunction: () => {
          handleToggleScreen();
        },
        seq: 3,
        permissionsRequired: false,
      },
      {
        element: FooterButton,
        title: 'Delete',
        footerButton: currentUser,
        icon: BinIcon,
        clickFunction: () =>
          handleSelect({ value: PRODUCT_DETAILS_PAGE_LABEL.DELETE_VALUE }, imageList?.[photoIndex]),
        seq: 4,
        hideInCaseOf: isGuestUser(),
        permissionsRequired: true,
      },
    ];

    const handleFooter = () => {
      const _footerActions = imageList?.[photoIndex]?.render_image_details
        ? footerActions
        : footerActions.filter((a) => !a.title?.toLowerCase()?.includes('edit in 3d'));
      return (
        <>
          {getImageTypeFooterComponent(pass, setPass, imageList, photoIndex)}
          <footer className={'footer-style preview-display-footer'}>
            <h1 className="title px-0">{imageList?.[photoIndex]?.name || ''}</h1>
            {_footerActions.length > 0 && (
              <ul className="footer-action-list ced-action">
                {_footerActions
                  ?.filter((a) => !a?.hideInCaseOf)
                  ?.filter(
                    ({ permissionsRequired }) =>
                      !permissionsRequired || permissionsRequired === canUpdatePdpV1Content
                  )
                  ?.map((a, index) => {
                    const { element, ...rest } = a;
                    const Component = element;
                    return (
                      <Component
                        // shareId={id}
                        id={`action${index}}`}
                        key={`action${index}}`}
                        {...rest}
                      />
                    );
                  })}
              </ul>
            )}
          </footer>
        </>
      );
    };

    const filterMenu = filteredSeparateActionMenu(currentUser, isPublicPage).filter(
      ({ permissionsRequired }) => !permissionsRequired || permissionsRequired === canUpdatePdpV1Content
    );

    const handleSelect = async (value, data) => {
      if (value?.value === PRODUCT_DETAILS_PAGE_LABEL.RENAME_VALUE) {
        setRenameValue(data);
      }
      if (value?.value === PRODUCT_DETAILS_PAGE_LABEL.EDIT_IN_3D_VALUE) {
        handleOpenCurator(data, dispatch);
      }
      if (value?.value === PRODUCT_DETAILS_PAGE_LABEL.DELETE_ALL_VALUE) {
        if (imageList?.length) {
          openInfoDialog({
            title: `Are you sure you want to delete all ${title}?`,
            description: '',
            cancelButtonText: 'No',
            buttonText: 'Yes',
            onButtonClick: async () => {
              // Separate regular images
              const imageIds = [];

              // Create promises array
              const deletePromises = [];

              imageList.forEach((image) => {
                if (image?.gif_file) {
                  deletePromises.push(apiDeleteProductGifs(image.id));
                } else {
                  imageIds.push(image.id);
                }
              });

              // Delete all images in a single call
              if (imageIds.length > 0) {
                const formData = getFormData(imageIds.map((id) => ({ product_room: id })));
                deletePromises.push(apiDeleteMultipleProductImages(formData));
              }

              const res = await Promise.allSettled(deletePromises);
              await fetchRenderData();
              if (res?.filter((a) => a.status === 'fulfilled')?.length > 0) {
                successToast('Items deleted successfully.');
                if (page > 1) {
                  setPage(1);
                  setHasMore(false);
                }
                // else {
                //   fetchRenderData();
                // }
              }
            },
            okBtnColor: data?.okBtnColor,
            cancelBtnColor: data?.cancelBtnColor,
            okBtnClassName: data?.okBtnClassName,
          });
        }
      }

      if (value?.value === PRODUCT_DETAILS_PAGE_LABEL.UPLOAD_VALUE) {
        setType(
          imageType === ROOM_TYPE.LIFESTYLE
            ? PRODUCT_ROOM_TYPE_VALUE.LIFESTYLE
            : PRODUCT_ROOM_TYPE_VALUE.VIGNETTE
        );
        setImageListForOpenModalType(imageList);
        setShowModal(true);
      }
      if (value?.value === PRODUCT_DETAILS_PAGE_LABEL.DELETE_VALUE) {
        openInfoDialog({
          title: `Are you sure you want to delete this ${data?.name}?`,
          description: '',
          cancelButtonText: 'No',
          buttonText: 'Yes',
          onButtonClick: async () => {
            const api = data?.gif_file ? apiDeleteProductGifs : apiDeleteProductImages;
            const res = await api(data?.id);
            if (res) {
              if (window?.document?.fullscreen || window.fullscreen) {
                handleToggleScreen();
              }
              successToast('Item deleted successfully.');
              setIsOpen(false);
              setPhotoIndex(0);
              setImageList(imageList.filter((_images) => _images.id !== data.id));
            }
          },
          okBtnColor: data?.okBtnColor,
          cancelBtnColor: data?.cancelBtnColor,
          okBtnClassName: data?.okBtnClassName,
        });
      }
      if (value?.value === PRODUCT_DETAILS_PAGE_LABEL.UPLOAD_GIF_VALUE) {
        setType(
          imageType === ROOM_TYPE.LIFESTYLE
            ? PRODUCT_ROOM_TYPE_VALUE.LIFESTYLE
            : PRODUCT_ROOM_TYPE_VALUE.VIGNETTE
        );
        setInitialTab(PRODUCT_ROOM_TYPE_VALUE.UPLOAD_GIF);
        setImageListForOpenModalType(imageList);
        setShowModal(true);
      }
      if (value?.value === PRODUCT_DETAILS_PAGE_LABEL.UPLOAD_IMAGE_VALUE) {
        setType(
          imageType === ROOM_TYPE.LIFESTYLE
            ? PRODUCT_ROOM_TYPE_VALUE.LIFESTYLE
            : PRODUCT_ROOM_TYPE_VALUE.VIGNETTE
        );
        setInitialTab(PRODUCT_ROOM_TYPE_VALUE.UPLOAD_IMAGES);
        setImageListForOpenModalType(imageList);
        setShowModal(true);
      }
    };

    const handleUpdateOrder = (value) => {
      if (canUpdatePdpV1Content && !imageList?.[value?.newIndex]?.gif_file) {
        if (value?.newIndex || value?.newIndex === 0) {
          const formData = new FormData();
          imageList.map((list, index) => {
            formData.append('id', list?.id);
            formData.append('sequence_number', index + 1);
          });
          handleUpdateOrderAPI(formData);
        }
      }
    };

    const handleUpdateOrderAPI = async (payload) => {
      await apiUpdateProductImageOrder(payload);
    };

    if (!canUpdatePdpV1Content && !imageList?.length) {
      return null;
    }

    const OnSave = async (name) => {
      if (loading) {
        return;
      }
      if (name !== renameValue?.name) {
        if (!renameValue?.name) {
          warningToast('Name is required.');
          return;
        }
        const formData = getFormData([{ name: renameValue?.name }]);
        setLoading(true);
        const api = renameValue?.gif_file ? apiUpdateProductGifs : apiUpdateProductImageName;
        const res = await api(renameValue?.id, formData);
        setLoading(false);
        if (res?.status && res?.data) {
          const indexVal = imageList.findIndex(({ id }) => +id === renameValue?.id);
          if (~indexVal) {
            const cloneImageryList = imageList.slice();
            cloneImageryList[indexVal]['name'] = res?.data?.name;
            setImageList(cloneImageryList);
            setRenameValue(null);
          } else {
            setRenameValue(null);
          }
        }
      } else {
        setRenameValue(null);
      }
    };

    const handleSortable = () => {
      const data = imageList?.map((item, index) => {
        return (
          <div className="life-style-body figure" key={item.id}>
            {item?.gif_file ? <div className="gif-type-placeholder">GIF</div> : null}
            <figure>
              <img
                className="life-style-first-image"
                src={item?.thumbnail || item?.image_file || item?.gif_file}
                alt={item?.name}
                onClick={() => {
                  setIsOpen(true);
                  setPhotoIndex(index);
                }}
              />
            </figure>
            <div className="life-style-title">
              {renameValue && +renameValue?.id === +item?.id ? (
                <div className="name-input-container">
                  <input
                    className={`form-control input-bg-color`}
                    value={renameValue?.name}
                    type="text"
                    onChange={(e) =>
                      setRenameValue({
                        ...renameValue,
                        name: e.target?.value,
                      })
                    }
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter') return;
                      OnSave(item?.name);
                    }}
                  />
                  <>
                    <Button
                      color="light"
                      onClick={() => {
                        setRenameValue(null);
                      }}
                      size="xlg"
                      disabled={loading}
                      className="so-icon-button"
                      iconBefore={<CancelIcon />}>
                      {' '}
                    </Button>
                    <Button
                      disabled={loading}
                      color="dark"
                      onClick={() => OnSave(item?.name)}
                      size="xlg"
                      className="so-icon-button"
                      loading={loading}
                      iconBefore={<CheckMarkIcon />}></Button>
                  </>
                </div>
              ) : (
                <>
                  <div>
                    <h6>{item?.name || title} </h6>
                    {item?.description && <p>{item?.description}</p>}
                  </div>
                  {filterMenu?.length > 0 && !isGuestUser() && (
                    <MenuDropdown
                      defaultPrevent
                      menu={
                        item?.render_image_details
                          ? filterMenu
                          : filterMenu.filter(
                              (a) => a.value !== PRODUCT_DETAILS_PAGE_LABEL.EDIT_IN_3D_VALUE
                            ) || []
                      }
                      list={item}
                      url={item?.image_file || item?.gif_file}
                      handleSelect={handleSelect}
                      btnClassName="p-0"
                    />
                  )}
                </>
              )}
            </div>
          </div>
        );
      });
      if (isMobile) {
        return (
          <Swiper
            spaceBetween={12}
            onSlideChange={(e) => {
              hasMore && e?.isEnd && setPage(page + 1);
            }}
            a11y={false}
            onSwiper={(swiper) => {}}
            navigation={{ nextEl: '.arrow-right-image', prevEl: '.arrow-left-image' }}
            className="row"
            breakpoints={{
              0: {
                slidesPerView: 1.5,
              },
              320: {
                slidesPerView: 1.5,
              },
              768: {
                slidesPerView: 2,
              },
              1199: {
                slidesPerView: 3,
              },
              1921: {
                slidesPerView: 4,
              },
              2150: {
                slidesPerView: 5,
              },
            }}>
            {data &&
              data?.length &&
              data?.map((item) => {
                return <SwiperSlide key={item.id}>{item}</SwiperSlide>;
              })}
          </Swiper>
        );
      } else {
        // return isPublicPage || !isEditor ? (
        return !canUpdatePdpV1Content ? (
          <div className="row">{data}</div>
        ) : (
          <ReactSortable
            expand={true}
            tag={CustomComponent}
            list={imageList}
            onSort={handleUpdateOrder}
            setList={canUpdatePdpV1Content ? setImageList : () => {}}>
            {data}
          </ReactSortable>
        );
      }
    };

    return (
      <>
        {isOpen && (
          <ImagePreviewSlider
            title={imageList?.[photoIndex]?.name || ''}
            imagesList={(imageList && imageList?.length && imageList?.map((list) => list)) || []}
            {...{ setIsOpen, photoIndex, setPhotoIndex, handleToggleScreen, pass, setPass }}
            footer={handleFooter()}
          />
        )}
        <div className={`life-style-header pt-md-4 pt-3 ${headerClassName}`}>
          <h6 className="main-title">{title}</h6>
          <div className="d-flex align-items-center">
            <button
              className={`arrow-left-image arrow ${
                !isMobile
                  ? 'hide-button'
                  : imageList?.length > 2 && browserWidth < 1189
                  ? ''
                  : imageList?.length < 4
                  ? 'hide-button'
                  : ''
              }`}>
              <LeftIcon />
            </button>
            <button
              className={`arrow-right-image arrow ${
                !isMobile
                  ? 'hide-button'
                  : imageList?.length > 2 && browserWidth < 1189
                  ? ''
                  : imageList?.length < 4
                  ? 'hide-button'
                  : ''
              }`}>
              <RightIcon />
            </button>
            {!isPublicPage && canUpdatePdpV1Content && !isGuestUser() && (
              <MenuDropdown
                defaultPrevent
                btnClassName="py-0 pe-0"
                menu={actionMenu}
                handleSelect={handleSelect}
              />
            )}
          </div>
        </div>

        {imageList && !imageList?.length && (
          <div className="empty-area mb-5 pb-3">
            {!loading && <span>{`Currently don't have any ${title.toLowerCase()}.`}</span>}
          </div>
        )}

        {imageList && imageList?.length > 0 && (
          <div
            id="imagenary-scroll"
            className="imagery-grid"
            style={{ padding: isMobile ? '0' : '0 6px' }}>
            {handleSortable()}
          </div>
        )}
      </>
    );
  }
);

const CustomComponent = forwardRef((props, ref) => {
  return (
    <div className="row" ref={ref}>
      {props.children}
    </div>
  );
});
